/**
 * Custom web elements for Rumpl header + mega + mobile drawer.
 * <site-header> — chrome, sticky, search, mega, drawer
 * <header-mega> — desktop panel host
 * <header-drawer> — mobile menu
 */

const CLOSE_DELAY_MS = 200;
const CLOSE_ANIM_MS = 500;
const MOBILE_MQ = '(max-width: 1024px)';

class HeaderMega extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'navigation');
    this.setAttribute('aria-label', 'Mega menu');
  }

  get panels() {
    return Array.from(this.querySelectorAll('[data-mega-panel]'));
  }

  showPanel(id) {
    const panels = this.panels;
    const match = panels.find((p) => p.getAttribute('data-mega-panel') === id);
    if (!match) return false;
    window.clearTimeout(this._hideTimer);
    this.classList.remove('is-closing');
    this.hidden = false;
    panels.forEach((p) => {
      p.hidden = p !== match;
    });
    return true;
  }

  hideAll() {
    this.classList.remove('is-closing');
    this.hidden = true;
    this.panels.forEach((p) => {
      p.hidden = true;
    });
  }

  beginClose(onDone) {
    if (this.hidden) {
      if (onDone) onDone();
      return;
    }
    this.classList.add('is-closing');
    window.clearTimeout(this._hideTimer);
    this._hideTimer = window.setTimeout(() => {
      this.hideAll();
      if (onDone) onDone();
    }, CLOSE_ANIM_MS);
  }
}

class HeaderDrawer extends HTMLElement {
  connectedCallback() {
    if (!this.id) this.id = 'rt-header-drawer';
    if (this._onBack) return;
    this._onBack = (event) => {
      const back = event.target.closest('[data-drawer-back]');
      if (!back) return;
      const drill = back.closest('[data-drawer-drill]');
      if (!drill) return;
      drill.removeAttribute('open');
      drill.querySelector(':scope > summary')?.focus();
    };
    this.addEventListener('click', this._onBack);
    this._onDrillToggle = (event) => {
      const drill = event.target;
      if (!(drill instanceof HTMLDetailsElement)) return;
      if (!drill.hasAttribute('data-drawer-drill') || !drill.open) return;
      this.querySelector('.rt-drawer__panel')?.scrollTo(0, 0);
      this.querySelector('.rt-drawer__body')?.scrollTo(0, 0);
    };
    this.addEventListener('toggle', this._onDrillToggle, true);
  }

  open() {
    window.clearTimeout(this._closeAnimTimer);
    this.classList.remove('is-closing');
    this.hidden = false;
    document.body.classList.add('rt-drawer-open');
    this._expandConfiguredNav();
  }

  _normalizeNavKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  _expandConfiguredNav() {
    const raw = (this.getAttribute('data-expand-nav') || '').trim();
    if (!raw) return;

    const needles = raw
      .split(',')
      .map((s) => this._normalizeNavKey(s))
      .filter(Boolean);
    if (!needles.length) return;

    const items = Array.from(this.querySelectorAll('details.rt-drawer__acc[data-drawer-nav]'));

    needles.forEach((needle) => {
      const match =
        items.find((el) => this._normalizeNavKey(el.getAttribute('data-drawer-nav')) === needle) ||
        items.find((el) => {
          const label = el.querySelector(':scope > summary')?.textContent || '';
          return this._normalizeNavKey(label) === needle;
        });
      if (!match) return;

      match.open = true;
      let parent = match.parentElement;
      while (parent && parent !== this) {
        if (parent.matches?.('details.rt-drawer__acc')) parent.open = true;
        parent = parent.parentElement;
      }
    });
  }

  close() {
    this.querySelectorAll('details[open]').forEach((view) => {
      view.open = false;
    });
    document.body.classList.remove('rt-drawer-open');
    this.classList.add('is-closing');
    window.clearTimeout(this._closeAnimTimer);
    this._closeAnimTimer = window.setTimeout(() => {
      this.hidden = true;
      this.classList.remove('is-closing');
    }, CLOSE_ANIM_MS);
  }
}

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this._bound = false;
    this._closeTimer = null;
    this._switchTimer = null;
    this._activeId = null;
    this._lastScrollTop = 0;
    this._onScroll = null;
    this._onResize = null;
    this._onKeydown = null;
    this._onDocumentClick = null;
    this._onMqChange = null;
    this._mq = null;
    this._onMegaScroll = () => this._updateBackdropTop();
  }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.setAttribute('role', 'banner');
    if (!this.id) this.id = 'main-header';

    this._mq = window.matchMedia(MOBILE_MQ);
    this._onMqChange = () => {
      if (!this.isMobile) {
        this.closeDrawer();
        this.closeMega();
        this.closeMobileCategory();
      } else {
        this.closeMega();
      }
    };
    if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMqChange);
    else if (this._mq.addListener) this._mq.addListener(this._onMqChange);

    this._onKeydown = (e) => {
      if (e.key === 'Escape') {
        this.closeMega();
        this.closeMobileCategory();
        this.closeSearch();
        this.closeDrawer();
      }
    };
    this._onDocumentClick = (event) => {
      if (this.searchPanel?.hidden !== false) return;
      if (this.searchPanel.contains(event.target)) return;
      if (event.target.closest('[data-search-trigger]')) return;
      this.closeSearch();
    };

    this.addEventListener('mouseenter', () => this._clearClose());
    this.addEventListener('mouseleave', () => this._scheduleClose());

    this.querySelectorAll('[data-mega-trigger]').forEach((el) => {
      el.addEventListener('mouseenter', (e) => this._onTriggerEnter(e));
      el.addEventListener('focus', (e) => this._onTriggerEnter(e));
      el.addEventListener('click', (e) => this._onTriggerClick(e));
    });

    this.megas.forEach((mega) => {
      mega.addEventListener('mouseenter', () => {
        this._clearSwitch();
        this._clearClose();
      });
    });

    // The backdrop sits inside <site-header>, so hovering it never fires the header's
    // mouseleave; treat entering it as leaving the menu.
    const backdrop = this.megaBackdrop;
    backdrop?.addEventListener('mouseenter', () => this._scheduleClose());
    backdrop?.addEventListener('click', () => this.closeMega());

    // Chips / nav links without a matching mega block must not keep another mega open
    for (const el of this.querySelectorAll('.rt-header__nav a:not([data-mega-trigger]), .rt-header__nav-secondary a:not([data-mega-trigger])')) {
      el.addEventListener('mouseenter', () => {
        if (this.isMobile) return;
        this._scheduleSwitch(() => this.closeMega());
      });
      el.addEventListener('focus', () => {
        if (this.isMobile) return;
        this.closeMega();
      });
      el.addEventListener('click', () => {
        this.closeMobileCategory();
      });
    }

    this.querySelectorAll('[data-search-trigger]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeDrawer();
        this.closeMobileCategory();
        this.toggleSearch();
      });
    });

    const searchClose = this.querySelector('[data-search-close]');
    if (searchClose) {
      searchClose.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeSearch();
      });
    }

    // Added: empty-search guard for non-predictive header form only
    // (predictive-search component handles its own submit)
    this.querySelectorAll('.rt-header__search-form').forEach((form) => {
      if (form.closest('predictive-search')) return;
      const input = form.querySelector('[data-search-input]');
      if (!input) return;
      input.addEventListener('input', () => input.setCustomValidity(''));
      form.addEventListener('submit', (e) => {
        if (input.value.trim()) return;
        e.preventDefault();
        input.setCustomValidity(input.dataset.emptyMessage || 'Please write anything');
        input.reportValidity();
      });
    });

    const drawerOpen = this.querySelector('[data-drawer-open]');
    if (drawerOpen) {
      drawerOpen.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMobileCategory();
        this.openDrawer();
      });
    }

    this.querySelectorAll('[data-drawer-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeDrawer();
      });
    });

    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onDocumentClick);
    this._initSticky();
    this._updateHeaderHeight();
  }

  disconnectedCallback() {
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    if (this._onDocumentClick) document.removeEventListener('click', this._onDocumentClick);
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('scroll', this._onMegaScroll);
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onResize);
    }
    if (this._mq && this._onMqChange) {
      if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMqChange);
      else if (this._mq.removeListener) this._mq.removeListener(this._onMqChange);
    }
    this._clearClose();
    this._clearSwitch();
    document.body.classList.remove('rt-drawer-open');
    document.body.classList.remove('rt-category-open');
    this._bound = false;
  }

  get megas() {
    return Array.from(this.querySelectorAll('header-mega'));
  }

  _megaForPanel(id) {
    return this.megas.find((host) => host.querySelector(`[data-mega-panel="${id}"]`)) || null;
  }

  get drawer() {
    return this.querySelector('header-drawer');
  }

  get megaBackdrop() {
    return this.querySelector('[data-mega-backdrop]');
  }

  get searchPanel() {
    return this.querySelector('[data-search-panel]');
  }

  get stickyType() {
    return this.getAttribute('sticky-type') || 'none';
  }

  get sectionEl() {
    return this.closest('.shopify-section') || this.parentElement;
  }

  get isMobile() {
    return this._mq ? this._mq.matches : window.matchMedia(MOBILE_MQ).matches;
  }

  openDrawer() {
    const drawer = this.drawer;
    const btn = this.querySelector('[data-drawer-open]');
    if (!drawer) return;
    this.closeMega();
    this.closeMobileCategory();
    this.closeSearch();
    drawer.open();
    if (btn) btn.setAttribute('aria-expanded', 'true');
  }

  closeDrawer() {
    const drawer = this.drawer;
    const btn = this.querySelector('[data-drawer-open]');
    if (!drawer) return;
    drawer.close();
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }

  openSearch() {
    const panel = this.searchPanel;
    const triggers = this.querySelectorAll('[data-search-trigger]');
    if (!panel) return;
    this.closeMega();
    this.closeMobileCategory();
    panel.hidden = false;
    this.classList.add('is-search-open');
    triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    const input = panel.querySelector('[data-search-input]');
    if (input) {
      requestAnimationFrame(() => {
        input.focus();
        input.select();
      });
    }
  }

  closeSearch() {
    const panel = this.searchPanel;
    const triggers = this.querySelectorAll('[data-search-trigger]');
    if (!panel) return;
    panel.hidden = true;
    this.classList.remove('is-search-open');
    triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }

  toggleSearch() {
    if (this.searchPanel?.hidden === false) this.closeSearch();
    else this.openSearch();
  }

  get mobileCategoryPanels() {
    return Array.from(this.querySelectorAll('[data-mobile-category]'));
  }

  hasMobileCategory(id) {
    return this.mobileCategoryPanels.some((panel) => panel.getAttribute('data-mobile-category') === id);
  }

  openMobileCategory(id) {
    window.clearTimeout(this._categoryCloseAnimTimer);
    let match = null;
    this.mobileCategoryPanels.forEach((panel) => {
      panel.classList.remove('is-closing');
      const active = panel.getAttribute('data-mobile-category') === id;
      panel.hidden = !active;
      if (active) match = panel;
    });
    if (!match) {
      this.closeMobileCategory();
      return false;
    }
    this.closeDrawer();
    this.closeSearch();
    this._activeId = id;
    this.classList.add('is-category-open');
    document.body.classList.add('rt-category-open');
    this.querySelectorAll('[data-mega-trigger]').forEach((el) => {
      el.classList.toggle('is-active', el.getAttribute('data-mega-trigger') === id);
    });
    return true;
  }

  closeMobileCategory() {
    const openPanels = this.mobileCategoryPanels.filter((panel) => !panel.hidden);
    this.classList.remove('is-category-open');
    document.body.classList.remove('rt-category-open');
    if (this.isMobile) {
      this._activeId = null;
      this.querySelectorAll('.rt-header__chip[data-mega-trigger]').forEach((el) => el.classList.remove('is-active'));
    }
    if (!openPanels.length) {
      this.mobileCategoryPanels.forEach((panel) => {
        panel.hidden = true;
        panel.classList.remove('is-closing');
      });
      return;
    }
    openPanels.forEach((panel) => panel.classList.add('is-closing'));
    window.clearTimeout(this._categoryCloseAnimTimer);
    this._categoryCloseAnimTimer = window.setTimeout(() => {
      this.mobileCategoryPanels.forEach((panel) => {
        panel.hidden = true;
        panel.classList.remove('is-closing');
      });
    }, CLOSE_ANIM_MS);
  }

  openMega(id) {
    if (this.isMobile) return;
    const host = this._megaForPanel(id);
    if (!host) {
      this.closeMega();
      return;
    }
    window.clearTimeout(this._megaCloseAnimTimer);
    this.megas.forEach((mega) => {
      if (mega === host) return;
      mega.hideAll();
    });
    host.classList.remove('is-closing');
    if (!host.showPanel(id)) {
      this.closeMega();
      return;
    }
    this.closeSearch();
    this._activeId = id;
    this.classList.add('is-mega-open');
    this._updateBackdropTop();
    window.addEventListener('scroll', this._onMegaScroll, { passive: true });
    this.querySelectorAll('[data-mega-trigger]').forEach((el) => {
      el.classList.toggle('is-active', el.getAttribute('data-mega-trigger') === id);
    });
  }

  closeMega() {
    this._clearClose();
    this._clearSwitch();
    this._activeId = null;
    this.classList.remove('is-mega-open');
    window.removeEventListener('scroll', this._onMegaScroll);
    this.querySelectorAll('[data-mega-trigger]').forEach((el) => el.classList.remove('is-active'));
    window.clearTimeout(this._megaCloseAnimTimer);
    this.megas.forEach((mega) => mega.beginClose());
  }

  _onTriggerEnter(event) {
    if (this.isMobile) return;
    const id = event.currentTarget.getAttribute('data-mega-trigger');
    if (!id) return;
    this._clearClose();
    if (this._activeId && this._activeId !== id) {
      this._scheduleSwitch(() => this.openMega(id));
      return;
    }
    this._clearSwitch();
    this.openMega(id);
  }

  _onTriggerClick(event) {
    const id = event.currentTarget.getAttribute('data-mega-trigger');
    if (!id) return;
    if (this.isMobile) return;
    if (event.currentTarget.tagName === 'BUTTON') {
      event.preventDefault();
      this._clearClose();
      const openHost = this._megaForPanel(id);
      if (this._activeId === id && openHost && !openHost.hidden) this.closeMega();
      else this.openMega(id);
    }
  }

  _scheduleClose() {
    if (this.isMobile) return;
    this._clearClose();
    this._clearSwitch();
    this._closeTimer = window.setTimeout(() => this.closeMega(), CLOSE_DELAY_MS);
  }

  _scheduleSwitch(fn) {
    this._clearSwitch();
    this._switchTimer = window.setTimeout(fn, CLOSE_DELAY_MS);
  }

  _clearSwitch() {
    if (this._switchTimer) {
      window.clearTimeout(this._switchTimer);
      this._switchTimer = null;
    }
  }

  _clearClose() {
    if (this._closeTimer) {
      window.clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  /** Starts the backdrop at the header's bottom edge, which moves as a non-sticky header scrolls. */
  _updateBackdropTop() {
    const bottom = Math.max(0, this.getBoundingClientRect().bottom);
    this.style.setProperty('--rt-mega-backdrop-top', `${bottom}px`);
  }

  _updateHeaderHeight() {
    const section = this.sectionEl;
    if (!section) return;
    document.documentElement.style.setProperty('--header-height', `${section.offsetHeight}px`);
  }

  _initSticky() {
    const type = this.stickyType;
    const section = this.sectionEl;
    if (!section || type === 'none') return;

    this._onResize = () => this._updateHeaderHeight();
    window.addEventListener('resize', this._onResize);
    window.addEventListener('load', this._onResize);

    this._onScroll = () => {
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

      if (type === 'on-scroll-up') {
        if (currentScroll > this._lastScrollTop) section.classList.add('header-sticky');
        else if (currentScroll < this._lastScrollTop) section.classList.remove('header-sticky');
        if (currentScroll === 0) section.classList.remove('header-sticky');
      }

      this._lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
    };

    window.addEventListener('scroll', this._onScroll, { passive: true });
  }
}

if (!customElements.get('header-mega')) {
  customElements.define('header-mega', HeaderMega);
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}
